import {
  VideoCallStatusUpdate,
  WSMessageTypes,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { useEffect, useRef } from 'react';

export const RefreshModules = {
  schedule: 'schedule',
  contacts: 'contacts',
  callActivity: 'callActivity',
};

/* Coalesces bursts of notifications into a single refresh per module. */
const REFRESH_DEBOUNCE_MS = 500;
/* Backoff between call-history refetches while the expected record is not yet persisted. */
const CALL_HISTORY_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

/* Statuses that produce a call history entry. */
const CALL_HISTORY_STATUSES = [
  VideoCallStatusUpdate.missed,
  VideoCallStatusUpdate.declined,
  VideoCallStatusUpdate.unreachable,
  VideoCallStatusUpdate.completed,
  VideoCallStatusUpdate.leaving,
];

const getModuleForMessage = (messageType, messageObj) => {
  if (messageType === WSMessageTypes.calendar) return RefreshModules.schedule;
  if (messageType === WSMessageTypes.contact) return RefreshModules.contacts;
  if (
    messageType === WSMessageTypes.videoCallStatus &&
    CALL_HISTORY_STATUSES.includes(messageObj?.status)
  )
    return RefreshModules.callActivity;
  return null;
};

/* react-query prefix keys invalidated per module. */
const MODULE_QUERY_KEYS = {
  [RefreshModules.contacts]: [['getContacts'], ['userHubs']],
};

const listeners = new Map();
const pending = new Map();

const notifyListeners = (module, detail) => {
  listeners.get(module)?.forEach((listener) => {
    try {
      listener(detail);
    } catch (error) {
      console.error(`Error refreshing ${module} after notification:`, error);
    }
  });
};

const flush = (queryClient, module) => {
  const { meetingIds } = pending.get(module);
  pending.delete(module);
  MODULE_QUERY_KEYS[module]?.forEach((queryKey) => queryClient?.invalidateQueries({ queryKey }));
  notifyListeners(module, { meetingIds: [...meetingIds] });
};

export const refreshModulesForMessage = (queryClient, messageType, messageObj) => {
  const module = getModuleForMessage(messageType, messageObj);
  if (!module) return;

  const entry = pending.get(module) ?? { meetingIds: new Set() };
  clearTimeout(entry.timer);
  if (module === RefreshModules.callActivity && messageObj?.meeting_id) {
    entry.meetingIds.add(messageObj.meeting_id);
  }
  entry.timer = setTimeout(() => flush(queryClient, module), REFRESH_DEBOUNCE_MS);
  pending.set(module, entry);
};

export const subscribeToModuleRefresh = (module, listener) => {
  if (!listeners.has(module)) listeners.set(module, new Set());
  listeners.get(module).add(listener);
  return () => listeners.get(module)?.delete(listener);
};

/* For views whose data is not held in react-query and must refetch on notification. */
export const useModuleRefresh = (module, onRefresh) => {
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  });
  useEffect(
    () => subscribeToModuleRefresh(module, (detail) => onRefreshRef.current?.(detail)),
    [module]
  );
};

const getMeetingId = (notification) =>
  notification?.call_status__meeting_id ?? notification?.meeting_id;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Refetches call history on call status notifications, retrying with backoff until every
 * notified meeting appears in the result or the retries run out.
 * `fetchCallHistory` must resolve to the fetched list, or null if the fetch failed or was superseded.
 */
export const useCallHistoryRefresh = (fetchCallHistory) => {
  const fetchRef = useRef(fetchCallHistory);
  useEffect(() => {
    fetchRef.current = fetchCallHistory;
  });

  useEffect(() => {
    let active = true;
    let generation = 0;
    const expectedMeetingIds = new Set();

    const run = async () => {
      const current = ++generation;
      const isCurrent = () => active && current === generation;
      for (let attempt = 0; ; attempt++) {
        const list = await fetchRef.current();
        if (!isCurrent()) return;
        list?.forEach((notification) => expectedMeetingIds.delete(getMeetingId(notification)));
        if (!expectedMeetingIds.size || attempt >= CALL_HISTORY_RETRY_DELAYS_MS.length) {
          expectedMeetingIds.clear();
          return;
        }
        await wait(CALL_HISTORY_RETRY_DELAYS_MS[attempt]);
        if (!isCurrent()) return;
      }
    };

    const unsubscribe = subscribeToModuleRefresh(
      RefreshModules.callActivity,
      ({ meetingIds = [] } = {}) => {
        meetingIds.forEach((meetingId) => expectedMeetingIds.add(meetingId));
        run();
      }
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);
};
